import { zodResolver } from '@hookform/resolvers/zod';
import {
    Box, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, Grid,
} from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useEffect } from 'react';
import { Form } from '@components/Form';
import MDButton from '@components/MDButton';
import { smallDialog } from 'assets/theme/dialogSize';
import { setLoading, useMaterialUIController } from 'context';
import { STAGE_NAME_MAX_LENGTH } from '@layouts/pages/CRM/kanban-board/constants/stage-name';
import { DefaultColumnService } from '../services/default-columns.service';
import { DefaultColumnRequest } from '../services/requests/default-columns.request';
import { DefaultColumnResponse } from '../services/responses/default-columns.response';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import { firstErrorMessage } from '@functions/first-error-message';

interface Props {
    open: boolean;
    edit: DefaultColumnResponse | null;
    handleModal: () => void;
    getAll: () => void;
    clearSearch: () => void;
}

export const DefaultColumnModal = ({ open, edit, handleModal, getAll, clearSearch }: Props) => {
    const { t } = useTranslation();
    const [, dispatch] = useMaterialUIController();
    const { showAlert } = useAlert();
    const service = new DefaultColumnService();

    const schema = z.object({
        name: z.string().min(1, t('defaultColumns.nomeObrigatorio')).max(STAGE_NAME_MAX_LENGTH),
    });

    const form = useForm<DefaultColumnRequest>({
        resolver: zodResolver(schema),
        defaultValues: { name: '' },
    });

    const { handleSubmit, reset, formState: { errors } } = form;

    useEffect(() => {
        if (open) {
            reset({ name: edit ? edit.name : '' });
        }
    }, [open]);

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            showAlert(firstErrorMessage(errors) ?? t('general.camposObrigatorios'), 'error', 2500);
        }
    }, [errors]);

    const saveChanges = async (data: DefaultColumnRequest) => {
        setLoading(dispatch, true);
        try {
            if (edit) {
                await service.update(edit.id, data);
                showAlert(t('general.atualizacao'), 'success', 2500);
                getAll();
            } else {
                await service.create(data);
                showAlert(t('general.cadastro'), 'success', 2500);
                clearSearch();
            }
            handleModal();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 3000);
        } finally {
            setLoading(dispatch, false);
        }
    };

    return (
        <Dialog open={open} onClose={handleModal} fullWidth maxWidth="sm" scroll="paper">
            <DialogTitle>{edit ? t('general.editar') : t('general.adicionar')}</DialogTitle>
            <FormProvider {...form}>
                <form onSubmit={handleSubmit(saveChanges)}>
                    <DialogContent style={smallDialog}>
                        <Grid container spacing={2}>
                            <Form.Field size={12}>
                                <FormControl fullWidth>
                                    <Form.Label>
                                        {t('general.nome')}
                                        <Form.MandatoryIcon />
                                    </Form.Label>
                                    <Form.Input type="text" autoComplete="off" size="medium" name="name" autoFocus inputProps={{ maxLength: STAGE_NAME_MAX_LENGTH }} />
                                    <Form.ErrorMessage field="name" />
                                </FormControl>
                            </Form.Field>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box></Box>
                        <Box>
                            <MDButton sx={{ marginRight: '5px' }} variant="gradient" color="dark" onClick={handleModal}>
                                {t('general.cancelar')}
                            </MDButton>
                            <MDButton variant="gradient" color="success" type="submit">
                                {t('general.salvar')}
                            </MDButton>
                        </Box>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};
